<template>
    <ui-dropdown placement="bottom-start" menu-class="w-96" :trigger-class="triggerClass">
        <template #default="dropdownScope">
            <slot v-bind="scope(dropdownScope)"></slot>
        </template>

        <template #menu="{ close }">
            <div :class="creatingNewMaterial ? 'p-5 pt-3' : 'p-3 max-h-64 overflow-y-auto'">

                <!-- New material form. -->
                <div v-if="creatingNewMaterial">
                    <div class="text-xs font-medium text-gray-400 mb-4">Create new material</div>
                    <ui-form-section>
                        <ui-input
                            label="Name"
                            name="name"
                            v-model="newMaterialForm.name"
                            :error="newMaterialForm.errors.get('name')"
                        ></ui-input>
                        <ui-input
                            label="Unit of Measure"
                            name="unit_of_measure"
                            v-model="newMaterialForm.unit_of_measure"
                            :error="newMaterialForm.errors.get('unit_of_measure')"
                        ></ui-input>
                    </ui-form-section>
                    <div class="mt-6 flex justify-end space-x-6">
                        <ui-button-new @click.stop.prevent="creatingNewMaterial = false">Cancel</ui-button-new>
                        <ui-button-new primary @click.stop.prevent="newMaterialForm.submit(close)" :loading="newMaterialForm.busy">
                            Create and Use Material
                        </ui-button-new>
                    </div>
                </div>

                <!-- Empty State. -->
                <div v-else-if="! query">
                    <div class="mb-4" v-if="current">
                        <div class="text-xs font-medium text-gray-400 mb-1">Current Material</div>
                        <p class="text-sm" v-text="current"></p>
                    </div>
                    <p class="text-gray-500 text-sm">Start typing to search for materials...</p>
                </div>

                <div v-else class="space-y-4">
                    <!-- New material. -->
                    <div>
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Create new material</div>
                        <button :class="buttonClass" class="flex items-center space-x-2" @click.stop.prevent="openMaterialForm">
                            <i class="fa fa-plus text-gray-400 text-sm"></i>
                            <div v-text="query"></div>
                        </button>
                    </div>

                    <!-- Existing Materials. -->
                    <div>
                        <div class="ml-2 text-xs font-medium text-gray-400 mb-1">Select existing material</div>
                        <div v-if="loading" class="ml-2 flex items-center space-x-2 text-gray-500 text-sm">
                            <svg class="animate-spin w-5 h-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div>Loading...</div>
                        </div>
                        <div v-else-if="materials.length === 0" class="ml-2 text-gray-500 text-sm">
                            <div>No existing material found.</div>
                        </div>
                        <div v-else class="space-y-1">
                            <button
                                v-for="material in materials"
                                :key="`material-${material.id}`"
                                class="flex flex-col"
                                :class="buttonClass"
                                :title="material.name"
                                @click.stop.prevent="selectMaterial(material, close)"
                            >
                                <div class="truncate w-full">{{ material.name }}</div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </ui-dropdown>
</template>

<script>
import { debounce } from 'lodash'
import { Material } from '@models'

export default {
    props: {
        uom: String,
        compact: Boolean,
        triggerClass: String,
        estimate: Object,
        current: String,
        buttonClass: {
            type: String,
            default: 'text-left w-full rounded-md px-2 py-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-600',
        },
    },
    data () {
        return {
            query: '',
            loading: false,
            materials: [],
            creatingNewMaterial: false,
            newMaterialForm: null,
        }
    },
    methods: {
        scope (dropdownScope) {
            return {
                query: this.query,
                setQuery: this.setQuery,
                ...dropdownScope
            }
        },
        setQuery (query) {
            this.query = query
            if (! this.query) return
            this.loading = true
            this.fetchMaterials()
        },
        selectMaterial (material, close) {
            this.$emit('selected', material)
            this.query = ''
            close()
        },
        openMaterialForm () {
            const initialData = {
                name: this.query,
                unit_of_measure: this.uom || null,
            }

            this.newMaterialForm = new Form(initialData, async (form, close) => {
                const material = await Material.create(form)
                this.$emit('created', material)
                this.creatingNewMaterial = false
                this.selectMaterial(material, close)
            }),
            this.creatingNewMaterial = true
        },
        fetchMaterials: debounce(async function () {
            try {
                this.loading = true
                this.materials = await Material.search(this.query, this.compact, this.estimate?.id, 20)
            } finally {
                this.loading = false
            }
        }, 300),
    },
}
</script>
